'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';


interface NavigationItem {
  label: string;
  href: string;
  icon?: string;
  requiresAuth?: boolean;
  adminOnly?: boolean;
}

interface ContextualNavigationProps {
  isAuthenticated?: boolean;
  isAdmin?: boolean;
}

const ContextualNavigation = ({ 
  isAuthenticated = false, 
  isAdmin = false 
}: ContextualNavigationProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (!isMounted) return;
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isMounted]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  const publicNavItems: NavigationItem[] = [
    { label: 'Home', href: '/landing-page', icon: 'HomeIcon' },
    { label: 'Health Solutions', href: '/health-vertical-pages', icon: 'HeartIcon' },
  ];

  const patientNavItems: NavigationItem[] = [
    { label: 'Dashboard', href: '/customer-dashboard', icon: 'ChartBarIcon', requiresAuth: true },
    { label: 'Health Solutions', href: '/health-vertical-pages', icon: 'HeartIcon' },
  ];

  const adminNavItems: NavigationItem[] = [
    { label: 'Admin Dashboard', href: '/admin-dashboard', icon: 'ChartBarIcon', adminOnly: true },
    { label: 'Content Management', href: '/content-management', icon: 'DocumentTextIcon', adminOnly: true },
  ];

  const getNavigationItems = (): NavigationItem[] => {
    if (isAdmin) {
      return adminNavItems;
    }
    if (isAuthenticated) {
      return patientNavItems;
    }
    return publicNavItems;
  };

  const navigationItems = getNavigationItems();

  const isActiveRoute = (href: string): boolean => {
    return pathname === href;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-100 transition-smooth ${
        isMounted && isScrolled 
          ? 'bg-card shadow-elevation-md' 
          : 'bg-card'
      }`}
    >
      <nav className="container mx-auto">
        <div className="flex items-center justify-between h-20 px-4 lg:px-8">
          <Link 
            href="/landing-page" 
            className="flex items-center gap-3 transition-smooth hover:opacity-80"
          >
            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
              <Icon name="HeartIcon" size={24} className="text-primary-foreground" variant="solid" />
            </div>
            <span className="text-xl font-heading font-semibold text-foreground">
              HealConnect
            </span>
          </Link>

          <div className="hidden lg:flex items-center gap-2">
            {navigationItems.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={`flex items-center gap-2 px-6 py-3 rounded-md transition-smooth font-medium ${
                  isActiveRoute(item.href)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-muted'
                }`}
              >
                {item.icon && (
                  <Icon 
                    name={item.icon as any} 
                    size={20} 
                    className={isActiveRoute(item.href) ? 'text-primary-foreground' : 'text-foreground'}
                  />
                )}
                {item.label}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-4">
            {!isAuthenticated ? (
              <>
                <Link
                  href="/login"
                  className="px-6 py-3 text-foreground font-medium transition-smooth hover:text-primary"
                >
                  Sign In
                </Link>
                <Link
                  href="/registration"
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-md font-medium transition-smooth hover:shadow-elevation-md hover:scale-[0.97] active:scale-[0.95]"
                >
                  Get Started
                </Link>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <button className="w-10 h-10 rounded-full bg-muted flex items-center justify-center transition-smooth hover:bg-primary hover:text-primary-foreground">
                  <Icon name="UserIcon" size={20} />
                </button>
              </div>
            )}
          </div>

          <button
            onClick={toggleMobileMenu}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-foreground transition-smooth hover:bg-muted rounded-md"
            aria-label="Toggle mobile menu"
          >
            <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="lg:hidden bg-card border-t border-border animate-slide-down">
            <div className="px-4 py-4 space-y-2">
              {navigationItems.map((item) => (
                <Link
                  key={item.href}
                  href={item.href}
                  className={`flex items-center gap-3 px-4 py-3 rounded-md transition-smooth font-medium ${
                    isActiveRoute(item.href)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  {item.icon && (
                    <Icon 
                      name={item.icon as any} 
                      size={20} 
                      className={isActiveRoute(item.href) ? 'text-primary-foreground' : 'text-foreground'}
                    />
                  )}
                  {item.label}
                </Link>
              ))}

              <div className="pt-4 border-t border-border space-y-2">
                {!isAuthenticated ? (
                  <>
                    <Link
                      href="/login"
                      className="flex items-center gap-3 px-4 py-3 text-foreground font-medium transition-smooth hover:bg-muted rounded-md"
                    >
                      <Icon name="ArrowRightOnRectangleIcon" size={20} />
                      Sign In
                    </Link>
                    <Link
                      href="/registration"
                      className="flex items-center gap-3 px-4 py-3 bg-primary text-primary-foreground rounded-md font-medium transition-smooth"
                    >
                      <Icon name="UserPlusIcon" size={20} />
                      Get Started
                    </Link>
                  </>
                ) : (
                  <button className="flex items-center gap-3 px-4 py-3 w-full text-foreground font-medium transition-smooth hover:bg-muted rounded-md">
                    <Icon name="UserIcon" size={20} />
                    My Account
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default ContextualNavigation;